import { useEffect, useState } from "react";
import { ArrowLeft, TrendUp, TrendDown, Minus, CalendarBlank, Plus, NotePencil } from "@phosphor-icons/react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  Area,
  ComposedChart,
} from "recharts";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { TREND_META, fmtDate } from "./utils";
// FP3-122 §17-22 — Canonical date-only helper. Chart X-axis and
// tooltip formatters must go through this so an ISO date like
// `2026-08-10` renders as "Aug 10" instead of the Arizona-shifted
// "Aug 9" caused by UTC-midnight parsing.
import { formatMonthDay } from "@/lib/dateOnly";
import AddProgressEntryDialog from "@/components/log/AddProgressEntryDialog";
// FP3-035F correction (Issue 2) — Hide Add Entry button when admin
// is shadowing the provider workspace.
// FP3-035F Issue 2 — Hide Add Entry button when admin is shadowing
// the provider workspace, so admins can read progress data but
// can't accidentally inject evidence rows.
// FP3-043 Phase A follow-up #9 — Same gate also applies when admin
// family users open a student progress drill-in via Admin → Progress.
import { useStudentWorkspaceReadOnly } from "@/components/layout/ReadOnlyProviderBanner";
import api from "@/lib/api";

const SOURCE_LABEL = {
  live_data: "Live Data",
  quick_session: "Quick Session",
  logged_session: "Logged Session",
  manual_entry: "Manual Entry",
  informal_assessment: "Informal Assessment",
};

const SOURCE_COLOR = {
  live_data: "bg-[#E8F0EB] text-[#1E3F33] border-[#1E3F33]/20",
  quick_session: "bg-[#FDF8F3] text-[#9C6B2E] border-[#9C6B2E]/20",
  logged_session: "bg-[#EEF1F8] text-[#3B5FAE] border-[#3B5FAE]/20",
  manual_entry: "bg-[#F3F1EC] text-[#1F2937] border-[#E5E3DB]",
  informal_assessment: "bg-[#F8EEF5] text-[#7E3F8C] border-[#7E3F8C]/20",
};

function TrendIcon({ status }) {
  if (status === "improving") return <TrendUp size={14} weight="bold" />;
  if (status === "declining") return <TrendDown size={14} weight="bold" />;
  return <Minus size={14} weight="bold" />;
}

// FP3-020 — Read-only collapsible "What this goal looks like in
// practice" panel. Renders only when the goal carries a saved
// practice_example. Default state: collapsed. Does NOT touch
// charts, evidence, sessions, or service minutes — purely a
// meeting-friendly explanation.
function PracticeExamplePanel({ example }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid="goal-practice-example-panel" className="mt-3">
      <button
        type="button"
        data-testid="goal-practice-example-toggle"
        onClick={() => setOpen((v) => !v)}
        className="text-[12px] text-[#4A6B8C] hover:text-[#1E3F33] underline decoration-dotted underline-offset-2"
      >
        {open ? "Hide “What this goal looks like in practice”" : "What this goal looks like in practice"}
      </button>
      {open && (
        <div
          data-testid="goal-practice-example-body"
          className="mt-2 p-3 rounded-md bg-[#F2F5F7] border border-[#D8DEE5] text-[12.5px] text-[#1F2937] leading-relaxed whitespace-pre-line"
        >
          {example}
        </div>
      )}
    </div>
  );
}

export default function GoalProgress({ goalId, onBack }) {
  const readOnly = useStudentWorkspaceReadOnly();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [windowDays, setWindowDays] = useState(90);
  const [addEntryOpen, setAddEntryOpen] = useState(false);

  const refresh = () => {
    if (!goalId) return;
    setLoading(true);
    api
      .getGoalProgress(goalId, { days: windowDays })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goalId, windowDays]);

  if (loading || !data) {
    return (
      <div className="space-y-3" data-testid="goal-progress-loading">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const { goal, student, trial_runs, evidence = [], computed_trend } = data;
  const trendKey = computed_trend?.status || "no_data";
  const meta = TREND_META[trendKey] || TREND_META.no_data;
  // FP3-130A — "Most recent %" card sources the latest chart point
  // from the canonical merged goal series (Live Data + Log Session
  // + Goal Evidence + corrections). Backend returns null when the
  // goal has no chartable data yet — FE renders "—". No more
  // reaching for the stale `goal.current_mastery` snapshot which
  // is only written by the single-trial-run POST.
  const mostRecent = data.most_recent_percent;
  const mostRecentDisplay =
    mostRecent === null || mostRecent === undefined
      ? "—"
      : `${Math.round(Number(mostRecent))}%`;
  const target = goal.target_mastery || 80;
  const baseAcc = parseInt((goal.baseline || "").match(/(\d+)\s*%/)?.[1] || "0");

  const chartData = trial_runs.map((r) => ({
    date: formatMonthDay(r.date),
    rawDate: r.date,
    accuracy: r.accuracy,
    trials: r.trials,
    source: r.source || "live_data",
  }));

  const narrativeOnly = evidence.filter((e) => !e.chartable);
  // FP3-122 §17-22 — Canonical goal average returned by the backend
  // (`canonical_average` / `canonical_session_count`) is the same
  // mean-of-per-session-percentages surfaced on Overview + Data tab.
  // Falls back to "—" when there is no numeric session data yet.
  const canonicalAverage = data.canonical_average;
  const canonicalSessionCount = data.canonical_session_count || 0;
  const averageDisplay =
    canonicalAverage === null || canonicalAverage === undefined
      ? "—"
      : `${canonicalAverage}%`;

  return (
    <div data-testid="goal-progress">
      {/* Header */}
      <div className="flex items-start gap-3 mb-5">
        <button
          onClick={onBack}
          data-testid="goal-back"
          className="w-8 h-8 rounded-lg border border-[#E5E3DB] flex items-center justify-center hover:bg-[#F3F1EC] shrink-0"
        >
          <ArrowLeft size={14} className="text-[#6B7280]" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <Badge variant="outline" className="text-[10px] capitalize border-[#E5E3DB] text-[#6B7280] px-1.5 py-0">
              {goal.category}
            </Badge>
            <Badge
              className="text-[10px] border-0 font-medium px-2 py-0 flex items-center gap-1"
              style={{ backgroundColor: meta.bg, color: meta.text }}
              data-testid="goal-trend-badge"
            >
              <TrendIcon status={trendKey} />
              {meta.label}
            </Badge>
            {student && (
              <span className="text-[11px] text-[#6B7280]">
                {student.first_name} {student.last_name} &middot; Gr {student.grade}
              </span>
            )}
          </div>
          <p className="text-sm text-[#1F2937] leading-snug">{goal.goal_text}</p>
          {/* FP3-020 — "What this goal looks like in practice" lives
              under the goal text. Collapsible; only renders when the
              goal carries a saved practice_example. Read-only. */}
          {goal.practice_example && (
            <PracticeExamplePanel example={goal.practice_example} />
          )}
        </div>
      </div>

      {/* Stats row — FP3-130A canonical layout:
          Baseline · Most recent % · Target · Average (mean of per-
          session percentages). "Most recent %" reads the latest
          point from the canonical merged series (Live Data + Log
          Session + Goal Evidence + corrections) — same source the
          chart uses. "Average" reads the same canonical goal-avg
          math shared by Caseload / Print / Admin Progress. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
        {[
          { label: "Baseline", value: `${baseAcc || "—"}%`, color: "#6B7280", testid: "stat-baseline" },
          {
            label: "Most recent %",
            value: mostRecentDisplay,
            color: meta.dot,
            testid: "stat-most-recent",
          },
          { label: "Target", value: `${target}%`, color: "#1E3F33", testid: "stat-target" },
          {
            label: "Average",
            value: averageDisplay,
            color: "#3B5FAE",
            testid: "stat-average",
            caption: `Across ${canonicalSessionCount} targeted session${canonicalSessionCount === 1 ? "" : "s"}`,
          },
        ].map((s) => (
          <div
            key={s.label}
            data-testid={s.testid}
            className="p-3 rounded-xl border border-[#E5E3DB] bg-[#F9F8F6]"
          >
            <p className="text-[10px] text-[#6B7280] font-medium uppercase tracking-wide">{s.label}</p>
            <p className="text-2xl font-semibold heading-font tabular-nums mt-1" style={{ color: s.color }}>
              {s.value}
            </p>
            {s.caption && (
              <p className="text-[10px] text-[#6B7280] mt-0.5" data-testid={`${s.testid}-caption`}>
                {s.caption}
              </p>
            )}
          </div>
        ))}
      </div>
      <div className="mb-5" />

      {/* Window toggle + add entry */}
      <div className="flex items-center justify-between mb-3 gap-2">
        <p className="text-xs text-[#6B7280]">
          {chartData.length} data point{chartData.length !== 1 ? "s" : ""} over {windowDays} days
        </p>
        <div className="flex items-center gap-2">
          {!readOnly.active && (
            <Button
              data-testid="goal-add-entry-btn"
              onClick={() => setAddEntryOpen(true)}
              size="sm"
              variant="outline"
              className="text-xs h-7 border-[#1E3F33]/30 text-[#1E3F33] hover:bg-[#E8F0EB]"
            >
              <Plus size={12} weight="bold" className="mr-1" />
              Add Entry
            </Button>
          )}
          <div className="flex bg-[#F3F1EC] rounded-lg p-0.5">
            {[
              { k: 30, label: "30d" },
              { k: 60, label: "60d" },
              { k: 90, label: "90d" },
              { k: 180, label: "180d" },
            ].map((b) => (
              <button
                key={b.k}
                data-testid={`goal-window-${b.k}`}
                onClick={() => setWindowDays(b.k)}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                  windowDays === b.k ? "bg-white text-[#1E3F33] shadow-sm" : "text-[#6B7280]"
                }`}
              >
                {b.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Chart */}
      <Card className="rounded-xl border border-[#E5E3DB] bg-white shadow-sm mb-5" data-testid="goal-chart">
        <CardContent className="p-4 pt-5">
          {chartData.length === 0 ? (
            <div className="text-center py-12 text-sm text-[#6B7280]">No trial data in this window.</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={chartData} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                <defs>
                  <linearGradient id="accFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={meta.dot} stopOpacity={0.25} />
                    <stop offset="100%" stopColor={meta.dot} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E3DB" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: "#6B7280" }}
                  tickLine={false}
                  axisLine={{ stroke: "#E5E3DB" }}
                />
                <YAxis
                  domain={[0, 100]}
                  tick={{ fontSize: 11, fill: "#6B7280" }}
                  tickLine={false}
                  axisLine={{ stroke: "#E5E3DB" }}
                  ticks={[0, 25, 50, 75, 100]}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#fff",
                    border: "1px solid #E5E3DB",
                    borderRadius: "8px",
                    fontSize: "12px",
                  }}
                  formatter={(v, name, props) => {
                    const src = props?.payload?.source || "live_data";
                    return [`${v}% · ${SOURCE_LABEL[src] || src}`, "Accuracy"];
                  }}
                />
                <ReferenceLine
                  y={target}
                  stroke="#1E3F33"
                  strokeDasharray="4 4"
                  strokeWidth={1}
                  label={{ value: `Target ${target}%`, position: "right", fontSize: 10, fill: "#1E3F33" }}
                />
                {baseAcc > 0 && (
                  <ReferenceLine
                    y={baseAcc}
                    stroke="#9B9890"
                    strokeDasharray="2 4"
                    strokeWidth={1}
                    label={{ value: `Baseline ${baseAcc}%`, position: "right", fontSize: 10, fill: "#9B9890" }}
                  />
                )}
                <Area type="monotone" dataKey="accuracy" stroke="none" fill="url(#accFill)" />
                <Line
                  type="monotone"
                  dataKey="accuracy"
                  stroke={meta.dot}
                  strokeWidth={2}
                  dot={{ r: 3, fill: meta.dot }}
                  activeDot={{ r: 5 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Trial runs table */}
      {trial_runs.length > 0 && (
        <div data-testid="trial-runs-list">
          <h3 className="text-sm font-semibold text-[#1F2937] heading-font mb-2">Data Points</h3>
          <div className="border border-[#E5E3DB] rounded-xl overflow-hidden bg-white">
            <table className="w-full text-xs">
              <thead className="bg-[#F9F8F6]">
                <tr className="text-left text-[10px] uppercase tracking-wide text-[#6B7280]">
                  <th className="px-3 py-2 font-medium">Date</th>
                  <th className="px-3 py-2 font-medium">Source</th>
                  <th className="px-3 py-2 font-medium">Trials</th>
                  <th className="px-3 py-2 font-medium">Correct</th>
                  <th className="px-3 py-2 font-medium">Accuracy</th>
                </tr>
              </thead>
              <tbody>
                {[...trial_runs].reverse().slice(0, 12).map((r, i) => {
                  const src = r.source || "live_data";
                  return (
                    <tr key={i} className="border-t border-[#E5E3DB]">
                      <td className="px-3 py-2 text-[#1F2937] font-medium">{fmtDate(r.date)}</td>
                      <td className="px-3 py-2">
                        <span
                          data-testid={`trial-source-${i}`}
                          className={`text-[10px] px-1.5 py-0.5 rounded-full border ${SOURCE_COLOR[src] || SOURCE_COLOR.manual_entry}`}
                        >
                          {SOURCE_LABEL[src] || src}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-[#6B7280]">{r.trials}</td>
                      <td className="px-3 py-2 text-[#6B7280]">{r.correct}</td>
                      <td
                        className="px-3 py-2 font-semibold tabular-nums"
                        style={{
                          color: r.accuracy >= target ? "#5A8A6A" : r.accuracy >= 50 ? "#1F2937" : "#B85C5C",
                        }}
                      >
                        {r.accuracy}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Narrative evidence timeline */}
      {narrativeOnly.length > 0 && (
        <div className="mt-5" data-testid="narrative-evidence-list">
          <h3 className="text-sm font-semibold text-[#1F2937] heading-font mb-2 flex items-center gap-1.5">
            <NotePencil size={14} weight="duotone" />
            Narrative Evidence
            <span className="text-[10px] text-[#6B7280] font-normal">
              · won't plot on chart
            </span>
          </h3>
          <div className="space-y-2">
            {narrativeOnly.slice(0, 12).map((e) => (
              <div
                key={e.id}
                data-testid={`narrative-row-${e.id}`}
                className="border border-[#E5E3DB] bg-white rounded-xl p-3"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] font-medium text-[#1F2937]">{fmtDate(e.date)}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#F3F1EC] text-[#6B7280] border border-[#E5E3DB]">
                    {e.source === "logged_session" ? "Logged Session" : "Manual Entry"}
                  </span>
                </div>
                <p className="text-xs text-[#1F2937] whitespace-pre-wrap leading-relaxed">
                  {e.narrative_note || <span className="text-[#9B9890] italic">— no note —</span>}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      <AddProgressEntryDialog
        open={addEntryOpen}
        onOpenChange={setAddEntryOpen}
        goal={goal}
        studentId={goal?.student_id}
        providerId={goal?.provider_id}
        onSaved={refresh}
      />
    </div>
  );
}
